import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, ArrowRight, Building2, FileSearch, LoaderCircle, Mic, Search, Sparkles, Square, Trash2, Volume2, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { api, errMsg } from "@/lib/api";
import { plainText } from "@/lib/builtbuddy";
import { useChatWidget } from "@/lib/chatWidget";
import { useReadAloud, useSpeechInput } from "@/hooks/useSpeech";
import { BuiltBuddyAvatar } from "@/components/builtbuddy/BuiltBuddyAvatar";
import { BuiltBuddyComposer } from "@/components/builtbuddy/BuiltBuddyComposer";

const READ_ALOUD_KEY = "bb_read_aloud";
const STATUS_ORDER = { in_progress: 0, scheduled: 1, on_hold: 2, quoting: 3, completed: 4 };
const STATUS_LABEL = { in_progress: "In progress", scheduled: "Scheduled", on_hold: "On hold", quoting: "Quoting", completed: "Completed" };
const statusTone = (s) =>
  s === "in_progress" ? "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]" : s === "completed" ? "bg-brand-steel/10 text-brand-steel-deep" : "bg-muted text-muted-foreground";

// Markdown styling shared with the engineer thread — tight, no chat-bubble chrome
const md = {
  p: (props) => <p className="mb-2 text-sm leading-6 last:mb-0" {...props} />,
  ul: (props) => <ul className="mb-2 list-disc space-y-1 pl-4 text-sm leading-6 last:mb-0" {...props} />,
  ol: (props) => <ol className="mb-2 list-decimal space-y-1 pl-4 text-sm leading-6 last:mb-0" {...props} />,
  li: (props) => <li className="text-sm leading-6" {...props} />,
  strong: (props) => <strong className="font-semibold text-foreground" {...props} />,
  h1: (props) => <p className="mb-1 mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground" {...props} />,
  h2: (props) => <p className="mb-1 mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground" {...props} />,
  h3: (props) => <p className="mb-1 mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground" {...props} />,
  a: (props) => <span className="underline decoration-primary/50" {...props} />,
  code: (props) => <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs" {...props} />,
  table: (props) => <table className="mb-2 w-full border-collapse text-xs" {...props} />,
  th: (props) => <th className="border border-border px-2 py-1 text-left font-semibold" {...props} />,
  td: (props) => <td className="border border-border px-2 py-1" {...props} />,
};

const fmtTime = (iso) => {
  const d = new Date(iso || "");
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
};

const Chips = ({ items, onPick, disabled, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {items.map((c, i) => (
      <Button
        key={c.id || i}
        variant="outline"
        size="sm"
        className="h-auto min-h-[30px] whitespace-normal rounded-full px-3 py-1 text-left text-[12px] font-medium leading-5"
        onClick={() => onPick?.(c.prompt || c.label || c)}
        disabled={disabled}
        data-testid={`${testId}-${c.id || i}`}
      >
        {c.label || c}
      </Button>
    ))}
  </div>
);

/** Opening state — a greeting, a few starter prompts and (quietly) the last job. The composer below is the hero. */
const Welcome = ({ greeting, intro, suggestions, loading, onPick, busy, lastJob, onContinueLast, firstName }) => (
  <div className="flex min-h-full flex-col justify-end gap-4 px-1 pb-2 pt-6" data-testid="builtbuddy-assistant-welcome">
    <div className="space-y-2">
      <p className="font-heading text-xl font-semibold leading-tight" data-testid="builtbuddy-assistant-greeting">
        {greeting || `G'day${firstName ? `, ${firstName}` : ""}.`} <span className="text-muted-foreground">What can I help with?</span>
      </p>
      <p className="text-sm leading-6 text-muted-foreground">{intro || "Ask me how to do anything in BuiltOS, a building-code question, or anything about running the job. Name a job if you want its live details."}</p>
    </div>
    <div>
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Try asking</p>
      {loading ? (
        <div className="flex flex-wrap gap-1.5">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-40 rounded-full" />)}</div>
      ) : (
        <Chips items={suggestions} onPick={onPick} disabled={busy} testId="builtbuddy-assistant-suggestion" />
      )}
    </div>
    {lastJob ? (
      <button
        type="button"
        onClick={onContinueLast}
        className="group inline-flex w-fit items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        data-testid="builtbuddy-assistant-continue-last"
      >
        <FileSearch className="h-3.5 w-3.5" /> Continue on {lastJob.name || "last job"} — open its drawings
        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </button>
    ) : null}
  </div>
);

const UserBubble = ({ m }) => (
  <article className={`ml-10 rounded-2xl rounded-br-md bg-primary px-3.5 py-2 text-primary-foreground shadow-sm ${m.pending ? "opacity-70" : ""}`} data-testid="builtbuddy-assistant-message-user" data-message-id={m.id}>
    <p className="whitespace-pre-wrap text-sm leading-6">{m.text}</p>
    <p className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-primary-foreground/70">
      {m.voice ? <Mic className="h-3 w-3" aria-label="Asked by voice" /> : null}
      {fmtTime(m.created_at)}
    </p>
  </article>
);

const AssistantCard = ({ m, onPick, onPickJob, onOpenJob, disabled, speaking, onToggleSpeak, ttsSupported }) => (
  <article className="mr-6 rounded-2xl rounded-bl-md border border-border bg-card px-3.5 py-3 shadow-[var(--shadow-card)]" data-testid="builtbuddy-assistant-message" data-message-id={m.id}>
    <div className="prose-sm max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{m.text || ""}</ReactMarkdown>
    </div>
    {m.kind === "clarify_job" && m.job_candidates?.length ? (
      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="builtbuddy-assistant-job-candidates">
        {m.job_candidates.map((j) => (
          <Button key={j.id} variant="outline" size="sm" className="h-auto min-h-[30px] max-w-full rounded-full px-3 py-1 text-left text-[12px] font-medium leading-5" onClick={() => onPickJob?.(j, m.pending_question)} disabled={disabled} data-testid={`builtbuddy-assistant-job-candidate-${j.id}`}>
            <Building2 className="mr-1.5 h-3 w-3 shrink-0 text-brand-steel-deep" /> <span className="truncate">{j.name || "Untitled job"}</span>
          </Button>
        ))}
      </div>
    ) : null}
    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
      {m.job_context ? (
        <button type="button" onClick={() => onOpenJob?.(m.job_context)} className="inline-flex items-center gap-1 rounded-full border border-brand-steel/40 bg-brand-steel/10 px-2 py-0.5 text-[10px] font-semibold text-brand-steel-deep hover:bg-brand-steel/20" title="Open this job's drawings & data in BuiltBuddy" data-testid={`builtbuddy-assistant-job-tag-${m.id}`}>
          <Building2 className="h-3 w-3" /> {m.job_context.name} <ArrowRight className="h-3 w-3" />
        </button>
      ) : null}
      <span className="font-mono-nums text-[10px] tabular-nums text-muted-foreground">{fmtTime(m.created_at)}</span>
      {ttsSupported ? (
        <button type="button" onClick={() => onToggleSpeak?.(m)} className="ml-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground" aria-pressed={speaking} data-testid={`builtbuddy-assistant-speak-${m.id}`}>
          {speaking ? <Square className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />} {speaking ? "Stop" : "Read aloud"}
        </button>
      ) : null}
    </div>
    {m.suggestions?.length ? (
      <div className="mt-2.5 border-t border-dashed border-border pt-2">
        <Chips items={m.suggestions.map((s, i) => ({ id: `${m.id}-${i}`, label: s, prompt: s }))} onPick={onPick} disabled={disabled} testId="builtbuddy-assistant-followup" />
      </div>
    ) : null}
  </article>
);

const Thinking = () => (
  <div className="mr-6 flex items-center gap-2 rounded-2xl rounded-bl-md border border-border bg-card px-3.5 py-2.5 text-xs text-muted-foreground shadow-[var(--shadow-card)]" data-testid="builtbuddy-assistant-thinking">
    <LoaderCircle className="h-3.5 w-3.5 animate-spin text-primary" /> Thinking…
  </div>
);

/** Inline "Switch job" picker — pick a job to load its drawings & data (the job-page BuiltBuddy). */
const JobPicker = ({ jobs, loading, error, onRetry, onOpenJob, onUseForChat, onBack, lastJob }) => {
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const shown = (jobs || [])
    .filter((j) => !needle || `${j.name || ""} ${j.address || ""} ${j.client_name || ""}`.toLowerCase().includes(needle))
    .sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) || String(a.name || "").localeCompare(String(b.name || "")))
    .slice(0, 30);
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="builtbuddy-assistant-job-picker">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack} aria-label="Back to chat" data-testid="builtbuddy-assistant-picker-back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search jobs…" className="h-9 pl-8 text-sm" autoFocus data-testid="builtbuddy-assistant-picker-search" />
        </div>
      </div>
      <p className="px-4 pt-3 text-xs text-muted-foreground">Pick a job to load its drawings and data — or set it as the job this chat is about.</p>
      {lastJob ? (
        <div className="px-3 pt-2">
          <Button variant="secondary" className="h-9 w-full justify-between" onClick={() => onOpenJob(lastJob)} data-testid="builtbuddy-assistant-picker-last">
            <span className="truncate">Continue on {lastJob.name || "last job"}</span>
            <ArrowRight className="h-4 w-4 shrink-0" />
          </Button>
        </div>
      ) : null}
      <ScrollArea className="min-h-0 flex-1">
        <div className="px-3 py-2" data-testid="builtbuddy-assistant-picker-list">
          {error ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">
              {error} <button type="button" className="underline" onClick={onRetry}>Retry</button>
            </p>
          ) : loading ? (
            <p className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground"><LoaderCircle className="h-3.5 w-3.5 animate-spin" /> Loading jobs…</p>
          ) : !shown.length ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">{needle ? "No job matches that search." : "No jobs yet — create one from the Jobs page first."}</p>
          ) : (
            shown.map((j) => (
              <div key={j.id} className="flex items-center gap-1 rounded-lg px-1 py-1 hover:bg-muted/50" data-testid={`builtbuddy-assistant-picker-item-${j.id}`}>
                <button type="button" onClick={() => onUseForChat(j)} className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-left focus-visible:outline-none" title="Ask about this job in the chat" data-testid={`builtbuddy-assistant-picker-use-${j.id}`}>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-navy/10 text-brand-steel-deep"><Building2 className="h-3.5 w-3.5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{j.name || "Untitled job"}</span>
                    {j.address ? <span className="block truncate text-[11px] text-muted-foreground">{j.address}</span> : null}
                  </span>
                  {j.status ? <Badge variant="outline" className={`h-5 shrink-0 rounded-full border-0 px-1.5 text-[9px] uppercase tracking-wide ${statusTone(j.status)}`}>{STATUS_LABEL[j.status] || j.status}</Badge> : null}
                </button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-brand-steel-deep" onClick={() => onOpenJob(j)} aria-label={`Open ${j.name || "job"} drawings`} data-testid={`builtbuddy-assistant-picker-open-${j.id}`}>
                      <FileSearch className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="text-xs">Open drawings &amp; data</TooltipContent>
                </Tooltip>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

/**
 * BuiltBuddy anywhere — a chat-first, docked panel (not a job list). Opening shows a greeting + starter prompts with the
 * composer front and centre. Job context is optional: the backend asks "which job?" when a question needs one and the
 * chips here pin it; "Switch job" loads a job's drawings & data (the job-page BuiltBuddy).
 */
export const BuiltBuddyAssistantPanel = ({ open, onOpenChange, lastJob, onNavigateJob, user }) => {
  const navigate = useNavigate();
  const widget = useChatWidget();
  const [view, setView] = useState("chat"); // chat | jobs
  const [messages, setMessages] = useState([]);
  const [threadLoaded, setThreadLoaded] = useState(false);
  const [jobContext, setJobContext] = useState(null);
  const [starter, setStarter] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [lastQuestion, setLastQuestion] = useState(null);
  const [input, setInput] = useState("");
  const [jobs, setJobs] = useState(null);
  const [jobsError, setJobsError] = useState("");
  const [readAloud, setReadAloudState] = useState(() => localStorage.getItem(READ_ALOUD_KEY) === "1");
  const [liveText, setLiveText] = useState("");
  const inputRef = useRef(null);
  const endRef = useRef(null);
  const voiceArmedRef = useRef(false);
  const tts = useReadAloud();
  const firstName = (user?.name || "").split(" ")[0];

  // thread + starter prompts on open
  useEffect(() => {
    if (!open) return;
    let alive = true;
    api.get("/bb-assistant/thread").then((r) => {
      if (!alive) return;
      setMessages(r.data?.messages || []);
      setJobContext(r.data?.job_context || null);
    }).catch(() => {}).finally(() => alive && setThreadLoaded(true));
    if (!starter) api.get("/bb-assistant/suggestions").then((r) => alive && setStarter(r.data)).catch(() => alive && setStarter({ suggestions: [] }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (view !== "jobs" || jobs) return;
    api.get("/jobs").then((r) => setJobs(Array.isArray(r.data) ? r.data : r.data?.jobs || [])).catch(() => setJobsError("Couldn't load your jobs — try again."));
  }, [view, jobs]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => endRef.current?.scrollIntoView({ block: "end" }), 60);
    return () => clearTimeout(t);
  }, [messages, busy, open, view]);

  const send = useCallback(
    async (text, { voice = false, jobId = null } = {}) => {
      const q = String(text ?? input).trim();
      if (!q || busy) return;
      setInput("");
      setError("");
      setView("chat");
      setLastQuestion({ text: q, voice, jobId });
      const optimistic = { id: `tmp-${Date.now()}`, role: "user", text: q, voice, created_at: new Date().toISOString(), pending: true };
      setMessages((m) => [...m, optimistic]);
      setBusy(true);
      try {
        const r = await api.post("/bb-assistant/chat", { message: q, voice, job_id: jobId || undefined });
        const reply = r.data;
        setMessages((m) => [...m.map((x) => (x.id === optimistic.id ? { ...x, pending: false } : x)), reply]);
        if (reply.job_context) setJobContext(reply.job_context);
        if ((readAloud || voice) && tts.supported && reply.kind !== "clarify_job") {
          setLiveText("Speaking answer");
          tts.speak(reply.id, plainText(reply.text));
        }
      } catch (e) {
        setMessages((m) => m.filter((x) => x.id !== optimistic.id));
        setError(errMsg(e, "BuiltBuddy couldn't answer just now"));
      } finally {
        setBusy(false);
      }
    },
    [input, busy, readAloud, tts]
  );

  const pickJobForQuestion = async (job, pendingQuestion) => {
    setJobContext({ id: job.id, name: job.name, status: job.status });
    api.post("/bb-assistant/context", { job_id: job.id }).catch(() => {});
    if (pendingQuestion) await send(pendingQuestion, { jobId: job.id });
  };

  const useJobForChat = (job) => {
    setJobContext({ id: job.id, name: job.name, status: job.status });
    api.post("/bb-assistant/context", { job_id: job.id }).catch(() => {});
    setView("chat");
    toast.message(`Talking about ${job.name || "this job"}`, { description: "Ask away — status, schedule, money, variations, defects. Use Switch job to load its drawings." });
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const clearJobContext = () => {
    setJobContext(null);
    api.post("/bb-assistant/context", { job_id: "" }).catch(() => {});
  };

  const openJob = (job, voice = false) => {
    onOpenChange(false);
    if (onNavigateJob) onNavigateJob(job, voice);
    else navigate(`/jobs/${job.id}?bb=1${voice ? "&bb_voice=1" : ""}`);
  };

  const clearThread = async () => {
    try {
      await api.delete("/bb-assistant/thread");
      setMessages([]);
      setJobContext(null);
      setError("");
      tts.stop();
      toast.success("Conversation cleared");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  // voice in
  const voiceIn = useSpeechInput({
    onTranscript: (t) => setInput(t),
    onEnd: (finalText) => {
      setLiveText("Stopped listening");
      const armed = voiceArmedRef.current;
      voiceArmedRef.current = false;
      if (armed && finalText.trim()) send(finalText, { voice: true, jobId: jobContext?.id || null });
    },
  });
  const toggleMic = () => {
    if (!voiceIn.supported) return toast.error("Voice input isn't supported in this browser — type your question instead");
    if (voiceIn.listening) return voiceIn.stop();
    tts.stop();
    voiceArmedRef.current = true;
    if (voiceIn.start(input)) setLiveText("Listening…");
  };
  const setReadAloud = (v) => {
    setReadAloudState(v);
    localStorage.setItem(READ_ALOUD_KEY, v ? "1" : "0");
    if (!v) tts.stop();
  };

  const conversational = useMemo(() => messages.filter((m) => m.role === "user" || m.role === "assistant"), [messages]);
  const showWelcome = threadLoaded && conversational.length === 0;

  return (
    <Sheet open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { voiceIn.stop(); tts.stop(); } }} modal={false}>
      <SheetContent
        side="right"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
        className="bottom-4 right-4 top-4 z-[45] flex h-[calc(100vh-2rem)] w-[420px] max-w-[92vw] flex-col gap-0 rounded-xl border border-border bg-card p-0 shadow-[var(--shadow-popover)] sm:max-w-[92vw] [&>button]:hidden"
        aria-label="BuiltBuddy assistant"
        data-testid="builtbuddy-assistant-panel"
      >
        <SheetHeader className="min-h-[56px] flex-row items-center justify-between gap-3 space-y-0 border-b border-border px-4 py-3 text-left" data-testid="builtbuddy-assistant-header">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-brand-steel/50 bg-brand-navy" aria-hidden>
              <BuiltBuddyAvatar style={widget.avatar_style} className="h-full w-full" />
            </span>
            <div className="min-w-0">
              <SheetTitle className="font-heading text-base leading-tight">BuiltBuddy</SheetTitle>
              <SheetDescription className="truncate text-[11px]">{widget.greeting_name} · site manager who knows BuiltOS inside out</SheetDescription>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant={view === "jobs" ? "secondary" : "ghost"} size="sm" className="h-8 gap-1.5 px-2 text-xs" onClick={() => setView(view === "jobs" ? "chat" : "jobs")} aria-pressed={view === "jobs"} data-testid="builtbuddy-assistant-switch-job">
                  <Building2 className="h-4 w-4" /> <span className="hidden sm:inline">Switch job</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Load a job&apos;s drawings &amp; data</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={clearThread} disabled={busy || conversational.length === 0} aria-label="Clear conversation" data-testid="builtbuddy-assistant-clear">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">Clear conversation</TooltipContent>
            </Tooltip>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenChange(false)} aria-label="Close BuiltBuddy" data-testid="builtbuddy-assistant-close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        {view === "jobs" ? (
          <JobPicker jobs={jobs} loading={!jobs && !jobsError} error={jobsError} onRetry={() => { setJobsError(""); setJobs(null); }} onOpenJob={openJob} onUseForChat={useJobForChat} onBack={() => setView("chat")} lastJob={lastJob} />
        ) : (
          <>
            {jobContext ? (
              <div className="flex items-center gap-2 border-b border-border bg-brand-steel/5 px-4 py-1.5 text-xs" data-testid="builtbuddy-assistant-context-bar">
                <Building2 className="h-3.5 w-3.5 text-brand-steel-deep" />
                <span className="min-w-0 flex-1 truncate">Talking about <span className="font-semibold">{jobContext.name}</span></span>
                <button type="button" onClick={() => openJob(jobContext)} className="text-[11px] font-medium text-brand-steel-deep hover:underline" data-testid="builtbuddy-assistant-context-open">Open drawings</button>
                <button type="button" onClick={clearJobContext} className="text-muted-foreground hover:text-foreground" aria-label="Clear job context" data-testid="builtbuddy-assistant-context-clear"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : null}
            <ScrollArea className="min-h-0 flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:!w-full [&_[data-radix-scroll-area-viewport]>div]:!min-w-0 [&_[data-radix-scroll-area-viewport]]:h-full" data-testid="builtbuddy-assistant-scroll">
              <div className="flex min-h-full flex-col px-3 py-3">
                {!threadLoaded ? (
                  <div className="space-y-3" data-testid="builtbuddy-assistant-loading"><Skeleton className="h-16" /><Skeleton className="ml-10 h-10" /><Skeleton className="h-24" /></div>
                ) : showWelcome ? (
                  <Welcome
                    greeting={starter?.greeting}
                    intro={starter?.intro}
                    suggestions={starter?.suggestions || []}
                    loading={!starter}
                    onPick={(p) => send(p, { jobId: jobContext?.id || null })}
                    busy={busy}
                    lastJob={lastJob}
                    onContinueLast={() => lastJob && openJob(lastJob)}
                    firstName={firstName}
                  />
                ) : (
                  <div className="space-y-3" data-testid="builtbuddy-assistant-thread">
                    {conversational.map((m) =>
                      m.role === "user" ? (
                        <UserBubble key={m.id} m={m} />
                      ) : (
                        <AssistantCard key={m.id} m={m} onPick={(p) => send(p, { jobId: jobContext?.id || null })} onPickJob={pickJobForQuestion} onOpenJob={openJob} disabled={busy} speaking={tts.speakingId === m.id} onToggleSpeak={(x) => tts.toggle(x.id, plainText(x.text))} ttsSupported={tts.supported} />
                      )
                    )}
                    {busy && <Thinking />}
                    {error && !busy && (
                      <Alert variant="destructive" data-testid="builtbuddy-assistant-error">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle className="text-sm">That one didn&apos;t go through.</AlertTitle>
                        <AlertDescription className="text-xs">
                          <span className="block">{error}</span>
                          {lastQuestion ? (
                            <Button variant="outline" size="sm" className="mt-2 h-7 text-xs" onClick={() => send(lastQuestion.text, { voice: lastQuestion.voice, jobId: lastQuestion.jobId })} data-testid="builtbuddy-assistant-retry">Try again</Button>
                          ) : null}
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}
                <div ref={endRef} />
              </div>
            </ScrollArea>
            <BuiltBuddyComposer
              ref={inputRef}
              value={input}
              onChange={setInput}
              onSend={() => send(undefined, { jobId: jobContext?.id || null })}
              busy={busy}
              listening={voiceIn.listening}
              onToggleMic={toggleMic}
              micSupported={voiceIn.supported}
              micBlocked={voiceIn.blocked}
              readAloud={readAloud}
              onReadAloudChange={setReadAloud}
              ttsSupported={tts.supported}
              liveText={liveText}
              disabledReason=""
              placeholder="Ask BuiltBuddy… e.g. ‘How do I issue an EOT?’ or ‘Status of my Box Hill job?’"
            />
            {showWelcome ? null : (
              <p className="sr-only" aria-live="polite">
                <Sparkles className="inline h-3 w-3" /> {conversational.length} messages
              </p>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
